import React, { useState, useEffect } from 'react';
import { Bookmark, Loader2, Trash2, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const WatchlistPage = () => {
  const { isAuthenticated, user, loginWithGoogle } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWatchlist();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchWatchlist = async () => {
    try {
      setLoading(true);
      const data = await apiService.getWatchlist();
      setWatchlist(data.watchlist || []);
    } catch (error) {
      console.error('Failed to fetch watchlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (marketId) => {
    try {
      await apiService.removeFromWatchlist(marketId);
      setWatchlist(prev => prev.filter(item => item.market_id !== marketId));
    } catch (error) {
      console.error('Failed to remove from watchlist:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="text-center">
            <Bookmark className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Your Watchlist</h2>
            <p className="text-gray-400 mb-6">Sign in to create and view your watchlist</p>
            <Button
              onClick={loginWithGoogle}
              className="bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] text-white rounded-full px-8 py-3 font-semibold"
            >
              Sign in with Google
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#131b2e] to-[#050810] border-b border-[#1e2a45]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-3 mb-2">
            <Bookmark className="h-8 w-8 text-[#3B82F6]" />
            <h1 className="text-3xl font-bold text-white">
              {user?.name ? `${user.name}'s Watchlist` : 'My Watchlist'}
            </h1>
          </div>
          <p className="text-gray-400">
            {watchlist.length} market{watchlist.length !== 1 ? 's' : ''} you're tracking
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
            <span className="ml-3 text-gray-400">Loading watchlist...</span>
          </div>
        ) : watchlist.length === 0 ? (
          <div className="text-center py-20">
            <Bookmark className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No markets in your watchlist</h3>
            <p className="text-gray-400 mb-6">Click the + button on any market to add it here</p>
            <Link to="/">
              <Button className="bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] text-white rounded-full px-8 py-3">
                Browse Markets
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {watchlist.map(item => {
              const market = item.market;
              const yesPrice = market.prices?.YES_price_bp ? Math.round(market.prices.YES_price_bp / 100) : 50;
              const noPrice = 100 - yesPrice;
              
              return (
                <div
                  key={item.id}
                  className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-4 hover:border-[#3B82F6]/30 transition-all"
                >
                  <div className="flex items-start gap-4">
                    {/* Market Image */}
                    <img
                      src={market.image || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=100&h=100&fit=crop'}
                      alt={market.title}
                      className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                    />
                    
                    {/* Market Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="text-white font-semibold line-clamp-1">{market.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-2 py-0.5 text-xs font-medium bg-[#1e2a45] text-gray-400 rounded-full">
                              {market.category}
                            </span>
                            <span className="text-xs text-gray-500">
                              Added {new Date(item.added_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleRemove(market.id)}
                          className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                          title="Remove from watchlist"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Prices */}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400">Yes:</span>
                          <span className="text-sm font-bold text-[#3B82F6]">{yesPrice}TK¢</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400">No:</span>
                          <span className="text-sm font-bold text-[#F97316]">{noPrice}TK¢</span>
                        </div>
                        <div className="flex items-center gap-1 ml-auto">
                          <TrendingUp className="h-4 w-4 text-green-400" />
                          <span className="text-sm text-green-400">{market.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default WatchlistPage;
